/**
 * Anthropic SDK adapter for browser automation tool
 */

import type { Tool } from '@anthropic-ai/sdk/resources/messages.mjs';
import { executeBrowserTask } from './core.js';
import type {
  BrowserConfig,
  BrowserTaskInput,
  BrowserTaskResult,
} from './types.js';
import { BROWSER_TOOL_DESCRIPTION, BROWSER_SYSTEM_PROMPT } from './prompts.js';

/**
 * Anthropic tool definition for browser automation
 */
export const browserTool: Tool = {
  name: 'browser_task',
  description: BROWSER_TOOL_DESCRIPTION,
  input_schema: {
    type: 'object',
    properties: {
      task: {
        type: 'string',
        description: 'Natural language description of what to do (e.g., "Test checkout flow for buying a pineapple")',
      },
      url: {
        type: 'string',
        description: 'Starting URL (e.g., https://3000-xyz.e2b.dev). Required if navigating to a specific page.',
      },
      maxSteps: {
        type: 'number',
        description: 'Maximum number of browser actions to take (1-50). Default: 10. Use 15-30 for complex flows.',
        default: 10,
      },
      region: {
        type: 'string',
        enum: ['sfo', 'lon'],
        description: 'Browserless region: sfo (US West Coast) or lon (Europe). Default: sfo.',
        default: 'sfo',
      },
    },
    required: ['task'],
  },
};

/**
 * Format browser task result for Anthropic tool result
 * 
 * Returns a concise summary suitable for agent context. The full result object
 * (with urls, errors, action_history, judgement, etc.) is available when calling
 * execute() directly, but this formatted string omits those details to save tokens.
 * 
 * @param result - Browser task result with full history data
 * @returns Formatted string summary for tool result
 */
function formatResult(result: BrowserTaskResult): string {
  if (result.success) {
    const parts = [
      '✅ Browser task completed successfully',
      `Steps taken: ${result.stepsTaken ?? 0}`,
      result.executionTimeMs ? `Execution time: ${result.executionTimeMs}ms` : null,
      '',
      'Result:',
      result.result || 'Task completed',
    ];
    return parts.filter(Boolean).join('\n');
  }

  return `❌ Browser task failed: ${result.error || 'Unknown error'}`;
}

/**
 * Create a configured browser tool with execute and formatResult methods
 * 
 * @param config - Browser worker configuration
 * @returns Tool definition with execute and formatResult methods
 * 
 * @example
 * ```typescript
 * import Anthropic from '@anthropic-ai/sdk';
 * import { createBrowserTool } from 'morphsdk/tools/browser/anthropic';
 * 
 * const tool = createBrowserTool({
 *   apiKey: process.env.MORPH_API_KEY,
 *   timeout: 180000
 * });
 * 
 * const client = new Anthropic();
 * 
 * const response = await client.messages.create({
 *   model: 'claude-sonnet-4-5-20250929',
 *   tools: [tool], // tool itself is the Tool definition
 *   messages: [{
 *     role: 'user',
 *     content: 'Test the checkout flow at https://3000-abc.e2b.dev'
 *   }]
 * });
 * 
 * // Execute and format
 * const result = await tool.execute(toolUseBlock.input);
 * const formatted = tool.formatResult(result);
 * ```
 */
export function createBrowserTool(config?: BrowserConfig) {
  return Object.assign({}, browserTool, {
    execute: async (input: BrowserTaskInput): Promise<BrowserTaskResult> => {
      return executeBrowserTask(input, config);
    },
    formatResult: (result: BrowserTaskResult): string => {
      return formatResult(result);
    },
    getSystemPrompt: (): string => {
      return BROWSER_SYSTEM_PROMPT;
    },
  });
}
